import { useMemo, useState } from 'react';
import {
  Avatar,
  Select,
  List,
  Card as AntdCard,
  Flex,
  Input,
  DatePicker,
  Button,
  Popconfirm,
  Row,
  Col,
  Tag,
  Tooltip,
} from 'antd';
import VirtualList from 'rc-virtual-list';
import {
  INotification,
  NotificationActionType,
  NotificationButton,
  NotificationTypes,
} from 'interfaces/notification';
import {
  GetNotificationsParams,
  useInfiniteNotifications,
  useMarkAllNotificationsAsSeen,
  useMarkNotificationSeen,
} from 'services/NotificationService';
import Card from 'components/card';
import {
  CheckOutlined,
  EyeOutlined,
  LoadingOutlined,
  MailOutlined,
  QuestionOutlined,
  RedoOutlined,
} from '@ant-design/icons';
import dayjs from 'util/dayjs';
import Title from 'antd/es/typography/Title';
import { TableDateTimeFormat } from 'constants/dateFormats';
import { capitalizeFirstLetter } from 'util/Tools';
import { useHistory } from 'react-router';

const CONTAINER_HEIGHT = 540;

const getTagForType = (type?: string) => {
  let color;
  switch (type) {
    case NotificationTypes.ROUTE_ASSIGN_REQUEST:
      color = 'blue';
      break;
    case NotificationTypes.GENERAL:
      color = 'lightgray';
      break;
    default:
      color = 'white';
  }
  return (
    <Tag color={color} style={{ padding: '2px 8px' }}>
      {type}
    </Tag>
  );
};

const getAvatarShape = (shape?: string) => {
  switch (shape) {
    case 'SQUARE':
      return 'square';
    case 'CIRCULAR':
      return 'circle';
    default:
      return 'square';
  }
};

const getActionType = (type?: string) => {
  switch (type) {
    case 'PRIMARY':
      return 'primary';
    default:
      return 'default';
  }
};

const getNotificationIcon = (icon?: string) => {
  switch (icon) {
    case 'APPROVED':
      return <CheckOutlined style={{ color: 'green', fontSize: '22px' }} />;
    case 'LETTER':
      return <MailOutlined style={{ color: 'darkcyan', fontSize: '22px' }} />;
    default:
      return <QuestionOutlined style={{ fontSize: '22px' }} />;
  }
};

const getButtons = (buttons?: NotificationButton[]) => {
  const history = useHistory();
  if (!buttons || buttons.length === 0) return;
  return buttons.map((button) => (
    <Button
      style={{ marginRight: '8px' }}
      type={getActionType(button.type)}
      onClick={() => {
        if (
          button.action.type === NotificationActionType.URL &&
          button.action.objectiveId
        )
          history.push(button.action.objectiveId);
      }}
    >
      {button.title}
    </Button>
  ));
};

const NotificationInbox = () => {
  const { mutate: markAllSeen } = useMarkAllNotificationsAsSeen();
  const { mutate: markAsSeen } = useMarkNotificationSeen();
  const [search, setSearch] = useState<string>('');
  const [status, setStatus] = useState<string>('');
  const [date, setDate] = useState<string>('');
  const params = useMemo(
    () =>
      ({
        search,
        status,
        fromDate: date ? dayjs(date).tz().startOf('day').utc().format() : '',
        toDate: date ? dayjs(date).tz().endOf('day').utc().format() : '',
      }) as GetNotificationsParams,
    [search, status, date],
  );
  const {
    data: notifications,
    fetchNextPage,
    isSuccess,
    isFetching,
    isFetchingNextPage,
    refetch,
  } = useInfiniteNotifications(params);

  const loading = isFetchingNextPage;

  const onScroll = (e: React.UIEvent<HTMLElement, UIEvent>) => {
    if (
      isSuccess &&
      notifications.data.length > 0 &&
      Math.abs(
        e.currentTarget.scrollHeight -
          e.currentTarget.scrollTop -
          CONTAINER_HEIGHT,
      ) <= 1
    ) {
      fetchNextPage();
    }
  };

  console.log(
    notifications?.data && notifications.data[0]
      ? dayjs(notifications.data[0].creationTimestamp)
      : 'Huh',
  );

  return (
    <>
      <Card title="Bandeja de Notificaciones">
        <Flex gap={8}>
          <Button
            type={isFetching ? 'default' : 'primary'}
            disabled={isFetchingNextPage}
            onClick={() => refetch()}
            style={{ padding: '0 8px' }}
          >
            {isFetching ? <LoadingOutlined /> : <RedoOutlined />}
          </Button>
          <Input.Search
            allowClear
            onSearch={(search) => setSearch(search)}
            placeholder="Escribí un término y presioná Enter para buscar"
          />
          <Select
            style={{ width: 300 }}
            placeholder="Estado"
            options={[
              { label: 'Todas', value: '' },
              { label: 'No leídas', value: 'unseen' },
              { label: 'Leídas', value: 'seen' },
            ]}
            defaultValue={''}
            onSelect={(status) => setStatus(status)}
          />
          <DatePicker
            style={{ width: 450 }}
            onChange={(dateString: string) => setDate(dateString)}
          />
          <Popconfirm
            title="Marcar todas como leídas"
            description="Esta acción no se puede deshacer"
            onConfirm={() => markAllSeen()}
            okText="Confirmar"
            cancelText="Cancelar"
          >
            <Button type="primary">Marcar todas como leídas</Button>
          </Popconfirm>
        </Flex>
      </Card>
      <AntdCard style={{ marginTop: 8 }}>
        <List loading={loading} split={false}>
          {notifications &&
            notifications.data &&
            notifications.data.length > 0 && (
              <VirtualList
                data={notifications.data}
                height={CONTAINER_HEIGHT}
                itemHeight={540 / 4}
                itemKey="id"
                onScroll={onScroll}
              >
                {(notification) => {
                  return (
                    <List.Item key={notification.id} style={{ paddingTop: 0 }}>
                      <AntdCard
                        style={{ width: '100%' }}
                        bodyStyle={{ padding: '12px' }}
                      >
                        <Row>
                          {notification?.images?.main && (
                            <Col span={2} style={{ width: '100%' }}>
                              <Flex justify="center" align="middle">
                                <Avatar
                                  size={{ md: 80, lg: 100, xl: 100, xxl: 100 }}
                                  shape={getAvatarShape(
                                    notification.images.main.shape,
                                  )}
                                  src={notification.images.main.src}
                                />
                              </Flex>
                            </Col>
                          )}
                          <Col
                            span={notification?.images?.main ? 22 : 24}
                            style={
                              notification?.images?.main
                                ? { paddingLeft: '24px' }
                                : undefined
                            }
                          >
                            <Row>
                              <Flex gap={16} align="center">
                                {getNotificationIcon(notification.icon)}
                                <Title
                                  level={4}
                                  style={{
                                    margin: 0,
                                  }}
                                >
                                  {notification.title}
                                </Title>
                                {getTagForType(notification.type)}
                                <Tooltip
                                  title={dayjs(notification.creationTimestamp)
                                    .tz()
                                    .format(TableDateTimeFormat)}
                                >
                                  <p
                                    style={{
                                      color: 'darkgrey',
                                      fontSize: '16px',
                                    }}
                                  >
                                    {capitalizeFirstLetter(
                                      dayjs(notification.creationTimestamp)
                                        .tz()
                                        .locale('es')
                                        .fromNow(),
                                    )}
                                  </p>
                                </Tooltip>
                              </Flex>
                            </Row>
                            <Row>
                              <Title
                                level={5}
                                style={{ color: '#777777', marginTop: '8px' }}
                              >
                                {notification.description}
                              </Title>
                            </Row>
                            <Row justify="space-between">
                              <Flex>{getButtons(notification.buttons)}</Flex>
                              {notification.seenTimestamp ? (
                                <Flex align="center">
                                  {notification.seenTimestamp && (
                                    <Tooltip
                                      title={dayjs(notification.seenTimestamp)
                                        .tz()
                                        .format(TableDateTimeFormat)}
                                    >
                                      <p
                                        style={{
                                          color: 'darkcyan',
                                          fontSize: '16px',
                                        }}
                                      >
                                        Leído{' '}
                                        {dayjs(notification.seenTimestamp)
                                          .tz()
                                          .locale('es')
                                          .fromNow()}
                                      </p>
                                    </Tooltip>
                                  )}
                                  <Button
                                    disabled
                                    style={{ marginLeft: '8px' }}
                                    icon={<EyeOutlined />}
                                  />
                                </Flex>
                              ) : (
                                <Button
                                  type="primary"
                                  onClick={() => {
                                    markAsSeen(notification.id);
                                  }}
                                  icon={<EyeOutlined />}
                                >
                                  Marcar como leído
                                </Button>
                              )}
                            </Row>
                          </Col>
                        </Row>
                      </AntdCard>
                    </List.Item>
                  );
                }}
              </VirtualList>
            )}
        </List>
      </AntdCard>
    </>
  );
};

export default NotificationInbox;
